import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Avatar, AvatarFallback, AvatarImage } from '../components/ui/avatar';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { WelcomeLetterEditor } from '../components/WelcomeLetterEditor';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  Search,
  Pencil,
  Send,
  Users,
  UserPlus,
  Shield,
  BookOpen,
  Clock,
  MoreVertical,
  Upload,
  Download,
  FileSpreadsheet,
  Briefcase,
  MapPin,
  Mail,
  GraduationCap,
  ChevronDown,
  CheckSquare,
} from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSub,
  DropdownMenuSubTrigger,
  DropdownMenuSubContent,
} from '../components/ui/dropdown-menu';
import axios from 'axios';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const UsersPage = () => {
  const { user } = useAuth();
  const [users, setUsers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [filterRole, setFilterRole] = useState('all');
  const [isLoading, setIsLoading] = useState(true);
  const [createOpen, setCreateOpen] = useState(false);
  const [createData, setCreateData] = useState({ name: '', email: '', password: '', role: 'instructor', school_district: '', job_role: '' });
  const [editUser, setEditUser] = useState(null);
  const [editSaving, setEditSaving] = useState(false);
  const [composeOpen, setComposeOpen] = useState(false);
  const [composeSubject, setComposeSubject] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [composeSending, setComposeSending] = useState(false);
  const [welcomeTplOpen, setWelcomeTplOpen] = useState(false);
  const [createError, setCreateError] = useState('');
  const [createLoading, setCreateLoading] = useState(false);

  // Demo account creation
  const [demoOpen, setDemoOpen] = useState(false);
  const [demoData, setDemoData] = useState({ name: '', email: '', password: '', expires_in_days: 14, note: '' });
  const [demoError, setDemoError] = useState('');
  const [demoLoading, setDemoLoading] = useState(false);

  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkPassword, setBulkPassword] = useState('');
  const [bulkRole, setBulkRole] = useState('instructor');
  const [roleChangeOpen, setRoleChangeOpen] = useState(false);
  const [roleEmails, setRoleEmails] = useState('');
  const [roleTarget, setRoleTarget] = useState('instructor');
  const [roleResult, setRoleResult] = useState(null);
  const [roleLoading, setRoleLoading] = useState(false);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [bulkResult, setBulkResult] = useState(null);
  const [bulkError, setBulkError] = useState('');
  const [selectedIds, setSelectedIds] = useState([]);
  const [blastLoading, setBlastLoading] = useState(false);

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get(`${API}/users`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreateUser = async () => {
    setCreateError('');
    if (!createData.name || !createData.email || !createData.password) {
      setCreateError('All fields are required');
      return;
    }
    if (createData.password.length < 6) {
      setCreateError('Password must be at least 6 characters');
      return;
    }
    setCreateLoading(true);
    try {
      await axios.post(`${API}/users`, createData);
      toast.success(`User "${createData.name}" created successfully!`);
      setCreateOpen(false);
      setCreateData({ name: '', email: '', password: '', role: 'instructor', school_district: '', job_role: '' });
      fetchUsers();
    } catch (err) {
      setCreateError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setCreateLoading(false);
    }
  };

  const handleCreateDemoUser = async () => {
    setDemoError('');
    if (!demoData.name || !demoData.email || !demoData.password) {
      setDemoError('Name, email, and password are required');
      return;
    }
    if (demoData.password.length < 6) {
      setDemoError('Password must be at least 6 characters');
      return;
    }
    if (demoData.expires_in_days < 1 || demoData.expires_in_days > 365) {
      setDemoError('Expiration must be 1–365 days');
      return;
    }
    setDemoLoading(true);
    try {
      const payload = { ...demoData, expires_in_days: parseInt(demoData.expires_in_days, 10) };
      if (!payload.note) delete payload.note;
      await axios.post(`${API}/users/demo`, payload);
      toast.success(`Demo account "${demoData.name}" created — expires in ${demoData.expires_in_days} days`);
      setDemoOpen(false);
      setDemoData({ name: '', email: '', password: '', expires_in_days: 14, note: '' });
      fetchUsers();
    } catch (err) {
      setDemoError(err.response?.data?.detail || 'Failed to create demo account');
    } finally {
      setDemoLoading(false);
    }
  };

  const handleCopyDemoLink = async (userId) => {
    try {
      const res = await axios.get(`${API}/users/${userId}/demo-login-token`);
      const link = `${window.location.origin}/demo-login?token=${res.data.token}`;
      await navigator.clipboard.writeText(link);
      toast.success('Demo login link copied to clipboard');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to generate demo link');
    }
  };

  const handleRenewDemo = async (userId, userName) => {
    try {
      const res = await axios.put(`${API}/users/${userId}/renew-demo?extra_days=14`);
      const newExpiry = res.data?.expires_at;
      const formatted = newExpiry ? new Date(newExpiry).toLocaleDateString() : '';
      toast.success(`Demo "${userName}" renewed — now expires ${formatted}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to renew demo');
    }
  };

  const ROLE_LABELS = {
    system_admin: 'System Admin',
    team_admin: 'Program Administrator',
    instructor: 'Instructor',
    skills_trainer: 'Skills Trainer',
  };

  const handleChangeRole = async (userId, role) => {
    try {
      await axios.put(`${API}/users/${userId}/role`, { role });
      toast.success(`Role changed to ${ROLE_LABELS[role]}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to change role');
    }
  };

  const handleSendWelcomeEmail = async (u) => {
    try {
      await axios.post(`${API}/users/${u.id}/send-welcome-email`, null, { params: { user_role: user?.role } });
      toast.success(`Welcome email sent to ${u.email}`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send welcome email');
    }
  };

  const handleEditUserSave = async () => {
    if (!editUser?.name?.trim() || !editUser?.email?.trim()) {
      toast.error('Name and email are required');
      return;
    }
    setEditSaving(true);
    try {
      await axios.put(`${API}/users/${editUser.id}`, {
        name: editUser.name.trim(),
        email: editUser.email.trim(),
        role: editUser.role,
        job_role: editUser.job_role || '',
        school_district: editUser.school_district || '',
      });
      toast.success('User info updated');
      setEditUser(null);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setEditSaving(false);
    }
  };

  const handleComposeSend = async () => {
    if (!composeSubject.trim() || !composeMessage.trim()) {
      toast.error('Subject and message are required');
      return;
    }
    setComposeSending(true);
    const tId = toast.loading(`Sending email to ${selectedIds.length} users (0/${selectedIds.length})…`);
    try {
      const res = await axios.post(`${API}/admin/send-custom-emails`, {
        user_ids: selectedIds,
        subject: composeSubject.trim(),
        message: composeMessage.trim(),
      });
      setComposeOpen(false);
      const jobId = res.data.job_id;
      const poll = setInterval(async () => {
        try {
          const { data: job } = await axios.get(`${API}/admin/email-job/${jobId}`);
          if (job.status === 'complete') {
            clearInterval(poll);
            setComposeSending(false);
            const failed = job.failed || [];
            if (failed.length > 0) {
              toast.error(`Done: ${job.summary}. Failed: ${failed.map((f) => f.email || f.user_id).join(', ')}`, { id: tId, duration: 10000 });
            } else {
              toast.success(`Email sent — ${job.summary}`, { id: tId });
            }
            setComposeSubject('');
            setComposeMessage('');
            setSelectedIds([]);
          } else {
            toast.loading(`Sending email (${job.done}/${job.total})…`, { id: tId });
          }
        } catch {
          clearInterval(poll);
          setComposeSending(false);
          toast.error('Lost track of the email job — check back shortly', { id: tId });
        }
      }, 2000);
    } catch (err) {
      setComposeSending(false);
      toast.error(err.response?.data?.detail || 'Failed to start the email blast', { id: tId });
    }
  };

  const toggleSelect = (id) => {
    setSelectedIds((prev) => prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]);
  };

  const handleBulkWelcome = async () => {
    if (!selectedIds.length) return;
    if (!window.confirm(`Send the welcome email to ${selectedIds.length} selected user(s)?`)) return;
    setBlastLoading(true);
    const tId = toast.loading(`Starting welcome email blast (0/${selectedIds.length})…`);
    try {
      const res = await axios.post(`${API}/admin/send-welcome-emails`, {
        user_ids: selectedIds,
        user_role: user?.role,
      });
      const jobId = res.data.job_id;
      const poll = setInterval(async () => {
        try {
          const { data: job } = await axios.get(`${API}/admin/email-job/${jobId}`);
          if (job.status === 'complete') {
            clearInterval(poll);
            setBlastLoading(false);
            const failed = job.failed || [];
            if (failed.length) {
              toast.warning(`${(job.sent || []).length} sent, ${failed.length} failed: ${failed.map((f) => f.email || f.user_id).join(', ')}`, { id: tId, duration: 12000 });
            } else {
              toast.success(`Welcome email sent to ${(job.sent || []).length} user(s)`, { id: tId, duration: 8000 });
            }
            setSelectedIds([]);
            fetchUsers();
          } else {
            toast.loading(`Sending welcome emails… ${job.done}/${job.total}`, { id: tId });
          }
        } catch (e) {
          clearInterval(poll);
          setBlastLoading(false);
          toast.error('Lost track of the email job — check the "Welcome email sent" notes below.', { id: tId });
          fetchUsers();
        }
      }, 2000);
    } catch (err) {
      setBlastLoading(false);
      toast.error(err.response?.data?.detail || 'Failed to start the email blast', { id: tId });
    }
  };

  const handleToggleActive = async (userId) => {
    try {
      const response = await axios.put(`${API}/users/${userId}/toggle-active`);
      toast.success(response.data.message);
      fetchUsers();
    } catch (err) {
      toast.error('Failed to update user status');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Are you sure you want to delete "${userName}"? This cannot be undone.`)) return;
    try {
      await axios.delete(`${API}/users/${userId}`);
      toast.success('User deleted');
      fetchUsers();
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const downloadTemplate = () => {
    const header = 'First Name,Last Name,Email,Job Role,School District,Portal Role\n';
    const sample = 'Jamie,Rivera,jamie.rivera@example.com,Job Coach,Lakota Local,Instructor\n';
    const blob = new Blob([header + sample], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'user-upload-template.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const handleBulkUpload = async () => {
    setBulkError('');
    const errs = [];
    if (!bulkFile) errs.push('choose a .csv or .xlsx file');
    if (!bulkPassword || bulkPassword.length < 6) errs.push('set a shared temporary password (min 6 characters)');
    if (errs.length) { setBulkError('Please ' + errs.join(' and ')); return; }
    setBulkLoading(true);
    setBulkResult(null);
    try {
      const fd = new FormData();
      fd.append('file', bulkFile);
      fd.append('shared_password', bulkPassword);
      fd.append('user_role', user?.role || 'instructor');
      fd.append('assign_role', bulkRole);
      const res = await axios.post(`${API}/users/bulk-upload`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setBulkResult(res.data);
      toast.success(`${res.data.created} user(s) created`);
      fetchUsers();
    } catch (err) {
      setBulkError(err.response?.data?.detail || 'Upload failed');
    } finally {
      setBulkLoading(false);
    }
  };

  const handleBulkRoleChange = async () => {
    const emails = roleEmails.split(/[\s,;]+/).map((e) => e.trim()).filter((e) => e.includes('@'));
    if (!emails.length) { toast.error('Paste at least one email address'); return; }
    setRoleLoading(true);
    try {
      const res = await axios.post(`${API}/users/bulk-role`, { emails, role: roleTarget }, { params: { user_role: user?.role, requester_id: user?.id } });
      setRoleResult(res.data);
      toast.success(`${res.data.updated.length} user(s) updated`);
      fetchUsers();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Role change failed');
    } finally {
      setRoleLoading(false);
    }
  };

  const downloadCredentials = () => {
    const esc = (v) => `"${String(v || '').replace(/"/g, '""')}"`;
    const rows = (bulkResult?.created_users || []).map((u) =>
      [esc(u.name), esc(u.email), esc(getRoleBadge(u.role).label), esc(bulkPassword)].join(',')
    );
    const csv = 'Name,Email,Role,Temporary Password\n' + rows.join('\n') + '\n';
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'new-user-credentials.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const getInitials = (name) => {
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      system_admin: { label: 'System Admin', class: 'bg-[#34657f] text-white' },
      team_admin: { label: 'Program Administrator', class: 'bg-[#b34e00] text-white' },
      instructor: { label: 'Instructor', class: 'bg-[#3d7a22] text-white' },
      skills_trainer: { label: 'Skills Trainer', class: 'bg-[#00707b] text-white' },
      demo: { label: 'Demo · View only', class: 'bg-amber-700 text-white' },
    };
    return roleConfig[role] || { label: 'User', class: 'bg-slate-500 text-white' };
  };

  const getRoleIcon = (role) => {
    const icons = {
      system_admin: Shield,
      team_admin: Users,
      instructor: BookOpen,
      skills_trainer: Briefcase,
    };
    return icons[role] || Users;
  };

  const lastNameOf = (n) => {
    const parts = (n || '').trim().split(/\s+/);
    return (parts.length > 1 ? parts[parts.length - 1] : parts[0] || '').toLowerCase();
  };

  const filteredUsers = users
    .filter((u) => {
      const matchesSearch = u.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        u.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = filterRole === 'all' || u.role === filterRole;
      return matchesSearch && matchesRole;
    })
    .sort((a, b) =>
      lastNameOf(a.name).localeCompare(lastNameOf(b.name)) ||
      (a.name || '').localeCompare(b.name || '')
    );

  const selectableUsers = filteredUsers.filter((u) => u.role !== 'demo');

  const formatDate = (dateString) => {
    if (!dateString) return 'Never';
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  };

  // Check if current user has admin access
  if (user?.role !== 'system_admin' && user?.role !== 'team_admin') {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="access-denied">
        <Card className="dashboard-card max-w-md text-center">
          <CardContent className="py-12">
            <Shield className="w-16 h-16 mx-auto mb-4 text-slate-300" />
            <h2 className="text-xl font-bold text-[#1A1A1A] mb-2">Access Restricted</h2>
            <p className="text-[#5C5C5C]">
              You don't have permission to view this page. Contact an administrator for access.
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]" data-testid="users-loading">
        <div className="w-8 h-8 border-4 border-[#34657f] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="users-page">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] text-[#1A1A1A]">
            User Management
          </h1>
          <p className="text-[#5C5C5C] mt-1">
            Manage portal users and their access levels
          </p>
        </div>
        <div className="flex flex-row gap-2">
          <Button
            className="bg-[#34657f] hover:bg-[#2a5060]"
            data-testid="add-user-btn"
            onClick={() => setCreateOpen(true)}
          >
            <UserPlus className="w-4 h-4 mr-2" />
            Add User
          </Button>
          <DropdownMenu modal={false}>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" data-testid="more-actions-btn">
                More Actions
                <ChevronDown className="w-4 h-4 ml-2" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-60">
              <DropdownMenuItem
                className="cursor-pointer"
                data-testid="bulk-upload-btn"
                onClick={() => { setBulkOpen(true); setBulkResult(null); setBulkError(''); setBulkFile(null); setBulkPassword(''); }}
              >
                <Upload className="w-4 h-4 mr-2" />
                Bulk Upload Users
              </DropdownMenuItem>
              {user?.role === 'system_admin' && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  data-testid="bulk-role-btn"
                  onClick={() => { setRoleChangeOpen(true); setRoleEmails(''); setRoleTarget('instructor'); setRoleResult(null); }}
                >
                  <Shield className="w-4 h-4 mr-2" />
                  Change Roles in Bulk
                </DropdownMenuItem>
              )}
              <DropdownMenuItem
                className="cursor-pointer"
                data-testid="add-demo-btn"
                onClick={() => setDemoOpen(true)}
              >
                <Clock className="w-4 h-4 mr-2" />
                Create Demo Account
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                data-testid="select-all-welcome-menu-item"
                onClick={() => {
                  setSelectedIds(selectableUsers.map((u) => u.id));
                  toast.info(`${selectableUsers.length} users selected — click "Send welcome email" above the list`);
                }}
              >
                <CheckSquare className="w-4 h-4 mr-2" />
                Select All for Welcome Email
              </DropdownMenuItem>
              <DropdownMenuItem
                className="cursor-pointer"
                data-testid="preview-welcome-email-btn"
                onClick={() => window.open(`${API}/emails/welcome-preview?name=${encodeURIComponent(user?.name?.split(' ')[0] || 'Taylor')}`, '_blank', 'noopener')}
              >
                <Mail className="w-4 h-4 mr-2" />
                Preview Welcome Email
              </DropdownMenuItem>
              {user?.role === 'system_admin' && (
                <DropdownMenuItem
                  className="cursor-pointer"
                  data-testid="edit-welcome-letter-btn"
                  onClick={() => setWelcomeTplOpen(true)}
                >
                  <Pencil className="w-4 h-4 mr-2" />
                  Edit Welcome Letter
                </DropdownMenuItem>
              )}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Stats — click a card to filter the list below */}
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
        {[
          { key: 'all', label: 'Total Users', icon: Users, color: '#34657f', count: users.length },
          { key: 'system_admin', label: 'System Admins', icon: Shield, color: '#b45309', count: users.filter((u) => u.role === 'system_admin').length },
          { key: 'team_admin', label: 'Program Administrators', icon: Briefcase, color: '#b34e00', count: users.filter((u) => u.role === 'team_admin').length },
          { key: 'instructor', label: 'Instructors', icon: BookOpen, color: '#3d7a22', count: users.filter((u) => u.role === 'instructor').length },
          { key: 'skills_trainer', label: 'Skills Trainers', icon: GraduationCap, color: '#1f7fa6', count: users.filter((u) => u.role === 'skills_trainer').length },
        ].map((s) => {
          const Icon = s.icon;
          const active = filterRole === s.key;
          return (
            <button
              key={s.key}
              type="button"
              onClick={() => setFilterRole(active && s.key !== 'all' ? 'all' : s.key)}
              className={`text-left rounded-xl border bg-white p-4 transition-all duration-200 hover:shadow-md hover:-translate-y-0.5 focus:outline-none ${
                active ? 'ring-2 shadow-md' : 'border-slate-200'
              }`}
              style={active ? { borderColor: s.color, boxShadow: `0 4px 14px ${s.color}22`, ['--tw-ring-color']: `${s.color}55` } : {}}
              data-testid={`${s.key === 'all' ? 'total-users' : s.key.replace('_', '-') + 's'}-stat`}
              title={s.key === 'all' ? 'Show all users' : `Show only ${s.label}`}
            >
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0">
                  <p className="text-3xl font-bold leading-none" style={{ color: s.color }}>{s.count}</p>
                  <p className="text-xs font-medium text-[#5C5C5C] mt-2 leading-snug">{s.label}</p>
                </div>
                <div className="w-9 h-9 rounded-lg flex items-center justify-center shrink-0" style={{ backgroundColor: `${s.color}1a` }}>
                  <Icon className="w-4.5 h-4.5" style={{ color: s.color, width: 18, height: 18 }} />
                </div>
              </div>
              <p className="text-[11px] mt-2" style={{ color: active ? s.color : '#64707a' }}>
                {active && s.key !== 'all' ? 'Filtering — click to clear' : s.key === 'all' ? 'Everyone on the portal' : 'Click to filter'}
              </p>
            </button>
          );
        })}
      </div>

      {/* Search and Filters */}
      <Card className="dashboard-card" data-testid="user-filters">
        <CardContent className="p-4">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
              <Input
                placeholder="Search users by name or email..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10 h-12 border-slate-300 focus:border-[#34657f]"
                data-testid="user-search-input"
              />
            </div>
            <Select value={filterRole} onValueChange={setFilterRole}>
              <SelectTrigger className="w-full sm:w-48 h-12" aria-label="Filter by role" data-testid="role-filter">
                <SelectValue placeholder="Filter by role" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Roles</SelectItem>
                <SelectItem value="system_admin">System Admin</SelectItem>
                <SelectItem value="team_admin">Program Administrator</SelectItem>
                <SelectItem value="instructor">Instructor</SelectItem>
                <SelectItem value="skills_trainer">Skills Trainer</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users List */}
      <div className="space-y-3">
        {filteredUsers.length > 0 && (
          <div className="flex items-center justify-between gap-3 flex-wrap bg-white border border-slate-200 rounded-lg px-4 py-2.5" data-testid="bulk-welcome-bar">
            <div className="flex items-center gap-3">
              <label className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A] cursor-pointer">
                <Checkbox
                  checked={selectableUsers.length > 0 && selectedIds.length === selectableUsers.length}
                  onCheckedChange={(checked) => setSelectedIds(checked ? selectableUsers.map((u) => u.id) : [])}
                  aria-label="Select all users shown"
                  data-testid="select-all-users-checkbox"
                />
                {selectedIds.length > 0 ? `${selectedIds.length} of ${selectableUsers.length} selected` : `Select all ${selectableUsers.length} users`}
              </label>
              {selectedIds.length === 0 && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setSelectedIds(selectableUsers.map((u) => u.id))}
                  data-testid="select-all-users-btn"
                >
                  <CheckSquare className="w-4 h-4 mr-1.5" />
                  Select All
                </Button>
              )}
            </div>
            {selectedIds.length > 0 && (
              <div className="flex items-center gap-2 flex-wrap">
                <Button
                  size="sm"
                  onClick={handleBulkWelcome}
                  disabled={blastLoading || composeSending}
                  className="bg-[#b34e00] hover:bg-[#a84d09] text-white"
                  data-testid="bulk-welcome-btn"
                >
                  <Mail className="w-4 h-4 mr-2" />
                  {blastLoading ? 'Sending…' : `Send welcome email to ${selectedIds.length} user${selectedIds.length > 1 ? 's' : ''}`}
                </Button>
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => setComposeOpen(true)}
                  disabled={blastLoading || composeSending}
                  className="border-[#34657f] text-[#34657f] hover:bg-[#34657f] hover:text-white"
                  data-testid="compose-email-btn"
                >
                  <Send className="w-4 h-4 mr-2" />
                  {composeSending ? 'Sending…' : 'Draft & send email'}
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setSelectedIds([])} data-testid="clear-selection-btn">
                  Clear
                </Button>
              </div>
            )}
          </div>
        )}
        {filteredUsers.length > 0 ? (
          filteredUsers.map((u) => {
            const roleBadge = getRoleBadge(u.role);
            const RoleIcon = getRoleIcon(u.role);
            
            return (
              <Card
                key={u.id}
                className="dashboard-card hover:border-[#34657f]/30 transition-colors"
                data-testid={`user-card-${u.id}`}
              >
                <CardContent className="p-5">
                  <div className="flex items-center gap-4">
                    {u.role !== 'demo' && (
                      <Checkbox
                        checked={selectedIds.includes(u.id)}
                        onCheckedChange={() => toggleSelect(u.id)}
                        aria-label={`Select ${u.name}`}
                        data-testid={`user-select-${u.id}`}
                      />
                    )}
                    <Avatar className="h-12 w-12">
                      <AvatarImage src={u.avatar_url} alt={u.name} />
                      <AvatarFallback className="bg-[#34657f] text-white font-semibold">
                        {getInitials(u.name)}
                      </AvatarFallback>
                    </Avatar>
                    
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-3 flex-wrap">
                        <h3 className="font-semibold text-[#1A1A1A]">{u.name}</h3>
                        <Badge className={`${roleBadge.class} text-xs`}>
                          <RoleIcon className="w-3 h-3 mr-1" />
                          {roleBadge.label}
                        </Badge>
                      </div>
                      <p className="text-sm text-[#5C5C5C] truncate">{u.email}</p>
                      {(u.job_role || u.school_district) && (
                        <div className="flex items-center gap-4 mt-1 flex-wrap" data-testid={`user-profile-${u.id}`}>
                          {u.job_role && (
                            <span className="flex items-center gap-1 text-xs text-[#5C5C5C]">
                              <Briefcase className="w-3.5 h-3.5 text-[#34657f]" /> {u.job_role}
                            </span>
                          )}
                          {u.school_district && (
                            <span className="flex items-center gap-1 text-xs text-[#5C5C5C]">
                              <MapPin className="w-3.5 h-3.5 text-[#b34e00]" /> {u.school_district}
                            </span>
                          )}
                        </div>
                      )}
                      {u.role !== 'demo' && u.welcome_email_sent_at && (
                        <p className="flex items-center gap-1 text-xs text-[#3d7a22] mt-0.5" data-testid={`welcome-sent-${u.id}`}>
                          <Mail className="w-3.5 h-3.5" aria-hidden="true" />
                          Welcome email sent {formatDate(u.welcome_email_sent_at)}
                        </p>
                      )}
                      {u.role === 'demo' && u.expires_at && (
                        <p
                          className={`text-xs mt-0.5 ${new Date(u.expires_at) < new Date() ? 'text-red-600 font-medium' : 'text-amber-600'}`}
                          data-testid={`demo-expiry-${u.id}`}
                        >
                          {new Date(u.expires_at) < new Date()
                            ? `Expired ${formatDate(u.expires_at)}`
                            : `Demo expires ${formatDate(u.expires_at)}`}
                        </p>
                      )}
                    </div>

                    <div className="hidden sm:flex items-center gap-6 text-sm text-[#5C5C5C]">
                      <div className="flex items-center gap-2">
                        <Clock className="w-4 h-4" />
                        <span>Last login: {formatDate(u.last_login)}</span>
                      </div>
                    </div>

                    <DropdownMenu modal={false}>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="icon" aria-label="User options" data-testid={`user-menu-${u.id}`}>
                          <MoreVertical className="w-4 h-4" />
                        </Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        {u.role === 'demo' && (
                          <DropdownMenuItem
                            onClick={() => handleCopyDemoLink(u.id)}
                            data-testid={`copy-demo-link-${u.id}`}
                          >
                            Copy demo login link
                          </DropdownMenuItem>
                        )}
                        {u.role === 'demo' && (
                          <DropdownMenuItem
                            onClick={() => handleRenewDemo(u.id, u.name)}
                            data-testid={`renew-demo-${u.id}`}
                          >
                            Renew Demo · +14 days
                          </DropdownMenuItem>
                        )}
                        {u.role !== 'demo' && (
                          <DropdownMenuItem
                            onClick={() => setEditUser({
                              id: u.id, name: u.name || '', email: u.email || '',
                              role: u.role, job_role: u.job_role || '', school_district: u.school_district || '',
                            })}
                            data-testid={`edit-user-${u.id}`}
                          >
                            <Pencil className="w-4 h-4 mr-2" />
                            Edit user info
                          </DropdownMenuItem>
                        )}
                        {u.role !== 'demo' && (
                          <DropdownMenuItem
                            onClick={() => handleSendWelcomeEmail(u)}
                            data-testid={`send-welcome-${u.id}`}
                          >
                            <Mail className="w-4 h-4 mr-2" />
                            {u.welcome_email_sent_at ? 'Resend welcome email' : 'Send welcome email'}
                          </DropdownMenuItem>
                        )}
                        {u.role !== 'demo' && (
                          <DropdownMenuSub>
                            <DropdownMenuSubTrigger data-testid={`change-role-${u.id}`}>
                              Change role
                            </DropdownMenuSubTrigger>
                            <DropdownMenuSubContent>
                              {Object.entries(ROLE_LABELS)
                                .filter(([key]) => key !== u.role)
                                .map(([key, label]) => (
                                  <DropdownMenuItem
                                    key={key}
                                    onClick={() => handleChangeRole(u.id, key)}
                                    data-testid={`change-role-${key}-${u.id}`}
                                  >
                                    {label}
                                  </DropdownMenuItem>
                                ))}
                            </DropdownMenuSubContent>
                          </DropdownMenuSub>
                        )}
                        <DropdownMenuItem
                          onClick={() => handleToggleActive(u.id)}
                          data-testid={`toggle-active-${u.id}`}
                        >
                          {u.is_active !== false ? 'Deactivate' : 'Activate'}
                        </DropdownMenuItem>
                        <DropdownMenuItem
                          className="text-red-600"
                          onClick={() => handleDeleteUser(u.id, u.name)}
                          data-testid={`delete-user-${u.id}`}
                        >
                          Delete User
                        </DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardContent>
              </Card>
            );
          })
        ) : (
          <Card className="dashboard-card">
            <CardContent className="py-12 text-center">
              <Users className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-[#1A1A1A] mb-2">
                No users found
              </h3>
              <p className="text-[#5C5C5C]">
                {searchQuery
                  ? 'Try adjusting your search query'
                  : 'Users will appear here when added'}
              </p>
            </CardContent>
          </Card>
        )}
      </div>

      {/* Create User Dialog */}
      <Dialog open={createOpen} onOpenChange={(open) => {
        setCreateOpen(open);
        if (!open) { setCreateData({ name: '', email: '', password: '', role: 'instructor', school_district: '', job_role: '' }); setCreateError(''); }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">Add New User</DialogTitle>
            <DialogDescription>Create a new portal account. They can log in immediately.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {createError && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg" data-testid="create-user-error">
                {createError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="user-name">Full Name</Label>
              <Input
                id="user-name"
                value={createData.name}
                onChange={(e) => setCreateData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Jane Smith"
                data-testid="create-user-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-email">Email Address</Label>
              <Input
                id="user-email"
                type="email"
                value={createData.email}
                onChange={(e) => setCreateData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="e.g. jane.smith@projectlife.edu"
                data-testid="create-user-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="user-password">Password</Label>
              <Input
                id="user-password"
                type="password"
                value={createData.password}
                onChange={(e) => setCreateData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="At least 6 characters"
                data-testid="create-user-password"
              />
            </div>
            <div className="space-y-2">
              <Label>Role</Label>
              <Select value={createData.role} onValueChange={(v) => setCreateData(prev => ({ ...prev, role: v }))}>
                <SelectTrigger data-testid="create-user-role">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="system_admin">System Admin — Full access</SelectItem>
                  <SelectItem value="team_admin">Program Administrator — Manages the program</SelectItem>
                  <SelectItem value="instructor">Instructor — Standard access</SelectItem>
                  <SelectItem value="skills_trainer">Skills Trainer — Standard access</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label htmlFor="user-district">District / Program Name</Label>
                <Input
                  id="user-district"
                  value={createData.school_district}
                  onChange={(e) => setCreateData(prev => ({ ...prev, school_district: e.target.value }))}
                  placeholder="e.g. Butler Tech"
                  data-testid="create-user-district"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="user-jobrole">Job Title (optional)</Label>
                <Input
                  id="user-jobrole"
                  value={createData.job_role}
                  onChange={(e) => setCreateData(prev => ({ ...prev, job_role: e.target.value }))}
                  placeholder="e.g. Transition Coordinator"
                  data-testid="create-user-jobrole"
                />
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCreateOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateUser}
              disabled={createLoading}
              className="bg-[#34657f]"
              data-testid="submit-create-user-btn"
            >
              {createLoading ? 'Creating...' : 'Create User'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit User Dialog */}
      <Dialog open={!!editUser} onOpenChange={(open) => { if (!open) setEditUser(null); }}>
        <DialogContent className="sm:max-w-md" data-testid="edit-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">Edit User Info</DialogTitle>
            <DialogDescription>Fix a wrong email, update the name, role, or district/program.</DialogDescription>
          </DialogHeader>
          {editUser && (
            <div className="space-y-4 pt-2">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Full Name</Label>
                <Input id="edit-name" value={editUser.name}
                  onChange={(e) => setEditUser(prev => ({ ...prev, name: e.target.value }))}
                  data-testid="edit-user-name" />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email Address</Label>
                <Input id="edit-email" type="email" value={editUser.email}
                  onChange={(e) => setEditUser(prev => ({ ...prev, email: e.target.value }))}
                  data-testid="edit-user-email" />
              </div>
              <div className="space-y-2">
                <Label>Role</Label>
                <Select value={editUser.role} onValueChange={(v) => setEditUser(prev => ({ ...prev, role: v }))}>
                  <SelectTrigger data-testid="edit-user-role">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="system_admin">System Admin</SelectItem>
                    <SelectItem value="team_admin">Program Administrator</SelectItem>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="skills_trainer">Skills Trainer</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="edit-district">District / Program Name</Label>
                  <Input id="edit-district" value={editUser.school_district}
                    onChange={(e) => setEditUser(prev => ({ ...prev, school_district: e.target.value }))}
                    data-testid="edit-user-district" />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="edit-jobrole">Job Title</Label>
                  <Input id="edit-jobrole" value={editUser.job_role}
                    onChange={(e) => setEditUser(prev => ({ ...prev, job_role: e.target.value }))}
                    data-testid="edit-user-jobrole" />
                </div>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditUser(null)}>Cancel</Button>
            <Button onClick={handleEditUserSave} disabled={editSaving} className="bg-[#34657f]" data-testid="edit-user-save-btn">
              {editSaving ? 'Saving…' : 'Save changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Compose Email Dialog */}
      <Dialog open={composeOpen} onOpenChange={setComposeOpen}>
        <DialogContent className="sm:max-w-lg" data-testid="compose-email-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl flex items-center gap-2">
              <Send className="w-5 h-5 text-[#34657f]" />
              Email {selectedIds.length} selected user{selectedIds.length !== 1 ? 's' : ''}
            </DialogTitle>
            <DialogDescription>
              Sent one-by-one with LIFE Link branding from your verified domain. Demo accounts are skipped.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="space-y-2">
              <Label htmlFor="compose-subject">Subject *</Label>
              <Input id="compose-subject" value={composeSubject}
                onChange={(e) => setComposeSubject(e.target.value)}
                placeholder="e.g. Reminder: monthly community call this Friday"
                data-testid="compose-subject-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="compose-message">Message *</Label>
              <Textarea id="compose-message" rows={7} value={composeMessage}
                onChange={(e) => setComposeMessage(e.target.value)}
                placeholder={"Hi everyone,\n\nSeparate paragraphs with a blank line — each recipient is greeted by their first name automatically."}
                data-testid="compose-message-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setComposeOpen(false)}>Cancel</Button>
            <Button onClick={handleComposeSend} disabled={composeSending} className="bg-[#34657f]" data-testid="compose-send-btn">
              <Send className="w-4 h-4 mr-1.5" />
              {composeSending ? 'Sending…' : `Send to ${selectedIds.length}`}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Welcome Letter Editor (system admins) */}
      <WelcomeLetterEditor open={welcomeTplOpen} onOpenChange={setWelcomeTplOpen} />

      {/* Create Demo Account Dialog */}
      <Dialog open={demoOpen} onOpenChange={(open) => {
        setDemoOpen(open);
        if (!open) { setDemoData({ name: '', email: '', password: '', expires_in_days: 14, note: '' }); setDemoError(''); }
      }}>
        <DialogContent className="sm:max-w-md" data-testid="create-demo-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl">Create Demo Account</DialogTitle>
            <DialogDescription>
              View-only, time-limited account for prospects and evaluators. Demo users can browse the
              Curriculum and Forum but cannot upload, post, comment, or like.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            {demoError && (
              <div className="bg-red-50 text-red-600 text-sm p-3 rounded-lg" data-testid="create-demo-error">
                {demoError}
              </div>
            )}
            <div className="space-y-2">
              <Label htmlFor="demo-name">Full Name</Label>
              <Input
                id="demo-name"
                value={demoData.name}
                onChange={(e) => setDemoData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="e.g. Prospective Partner"
                data-testid="create-demo-name"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="demo-email">Email Address</Label>
              <Input
                id="demo-email"
                type="email"
                value={demoData.email}
                onChange={(e) => setDemoData(prev => ({ ...prev, email: e.target.value }))}
                placeholder="e.g. evaluator@example.com"
                data-testid="create-demo-email"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="demo-password">Password</Label>
              <Input
                id="demo-password"
                type="password"
                value={demoData.password}
                onChange={(e) => setDemoData(prev => ({ ...prev, password: e.target.value }))}
                placeholder="At least 6 characters"
                data-testid="create-demo-password"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="demo-days">Expires In (days)</Label>
              <Input
                id="demo-days"
                type="number"
                min={1}
                max={365}
                value={demoData.expires_in_days}
                onChange={(e) => setDemoData(prev => ({ ...prev, expires_in_days: e.target.value }))}
                data-testid="create-demo-days"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="demo-note">Note (optional)</Label>
              <Input
                id="demo-note"
                value={demoData.note}
                onChange={(e) => setDemoData(prev => ({ ...prev, note: e.target.value }))}
                placeholder="e.g. Demo for Butler Tech board"
                data-testid="create-demo-note"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDemoOpen(false)}>Cancel</Button>
            <Button
              onClick={handleCreateDemoUser}
              disabled={demoLoading}
              className="bg-amber-500 hover:bg-amber-600 text-white"
              data-testid="submit-create-demo-btn"
            >
              {demoLoading ? 'Creating...' : 'Create Demo Account'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk Upload Dialog */}
      <Dialog open={bulkOpen} onOpenChange={(open) => { setBulkOpen(open); if (!open) { setBulkResult(null); setBulkError(''); } }}>
        <DialogContent className="sm:max-w-lg" data-testid="bulk-upload-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl flex items-center gap-2">
              <Upload className="w-5 h-5 text-[#34657f]" />
              Bulk upload users
            </DialogTitle>
            <DialogDescription>
              Upload a spreadsheet to create many accounts at once. Columns: First Name, Last Name, Email, Job Role, School District — plus an optional "Portal Role" column.
            </DialogDescription>
          </DialogHeader>

          {!bulkResult ? (
            <div className="space-y-4">
              <button
                type="button"
                onClick={downloadTemplate}
                className="flex items-center gap-2 text-sm text-[#34657f] hover:underline"
                data-testid="download-template-btn"
              >
                <Download className="w-4 h-4" /> Download CSV template
              </button>

              <div className="space-y-1.5">
                <Label htmlFor="bulk-file">Spreadsheet (.csv or .xlsx)</Label>
                <Input
                  id="bulk-file"
                  type="file"
                  accept=".csv,.xlsx"
                  onChange={(e) => setBulkFile(e.target.files?.[0] || null)}
                  data-testid="bulk-file-input"
                />
                {bulkFile && (
                  <p className="text-xs text-[#5C5C5C] flex items-center gap-1">
                    <FileSpreadsheet className="w-3.5 h-3.5" /> {bulkFile.name}
                  </p>
                )}
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="bulk-password">Shared temporary password</Label>
                <Input
                  id="bulk-password"
                  type="text"
                  value={bulkPassword}
                  onChange={(e) => setBulkPassword(e.target.value)}
                  placeholder="e.g. Welcome2026"
                  data-testid="bulk-password-input"
                />
                <p className="text-xs text-[#5C5C5C]">Everyone in this batch gets this password to sign in. Share it with them and ask them to keep it private.</p>
              </div>

              <div className="space-y-1.5">
                <Label htmlFor="bulk-role">Role for these users</Label>
                <Select value={bulkRole} onValueChange={setBulkRole}>
                  <SelectTrigger id="bulk-role" data-testid="bulk-role-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="skills_trainer">Skills Trainer</SelectItem>
                    <SelectItem value="team_admin">Program Administrator</SelectItem>
                    <SelectItem value="system_admin">System Admin</SelectItem>
                  </SelectContent>
                </Select>
                <p className="text-xs text-[#5C5C5C]">Applies to every row — or add a "Portal Role" column in your file to mix roles in one upload.</p>
              </div>

              <div className="rounded-lg bg-slate-50 border border-slate-200 p-3 text-xs text-[#5C5C5C]">
                Rows without a valid email, or emails that already exist, are skipped and reported. Everyone must set a new password on first login.
              </div>

              {bulkError && <p className="text-sm text-red-600" data-testid="bulk-error">{bulkError}</p>}
            </div>
          ) : (
            <div className="space-y-3" data-testid="bulk-result">
              <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                <strong>{bulkResult.created}</strong> user(s) created out of {bulkResult.total_rows} row(s).
              </div>
              {bulkResult.skipped?.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <p className="text-sm font-medium text-amber-800 mb-1">{bulkResult.skipped.length} skipped:</p>
                  <ul className="text-xs text-amber-800 space-y-0.5 max-h-40 overflow-auto">
                    {bulkResult.skipped.map((s, i) => (
                      <li key={i}>Row {s.row}: {s.email || '(no email)'} — {s.reason}</li>
                    ))}
                  </ul>
                </div>
              )}
              <p className="text-xs text-[#5C5C5C]">New users can sign in with their email and the shared temporary password you set.</p>
              {bulkResult.created_users?.length > 0 && (
                <Button variant="outline" className="w-full" onClick={downloadCredentials} data-testid="download-credentials-btn">
                  <Download className="w-4 h-4 mr-2" />
                  Download credentials CSV ({bulkResult.created_users.length} users)
                </Button>
              )}
            </div>
          )}

          <DialogFooter>
            {!bulkResult ? (
              <>
                <Button variant="outline" onClick={() => setBulkOpen(false)} data-testid="bulk-cancel-btn">Cancel</Button>
                <Button className="bg-[#34657f] hover:bg-[#2a5060]" onClick={handleBulkUpload} disabled={bulkLoading} data-testid="bulk-submit-btn">
                  {bulkLoading ? 'Uploading…' : 'Upload & create users'}
                </Button>
              </>
            ) : (
              <Button className="bg-[#34657f] hover:bg-[#2a5060]" onClick={() => setBulkOpen(false)} data-testid="bulk-done-btn">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Bulk role change dialog (system admins) */}
      <Dialog open={roleChangeOpen} onOpenChange={(open) => { setRoleChangeOpen(open); if (!open) setRoleResult(null); }}>
        <DialogContent className="sm:max-w-lg" data-testid="bulk-role-dialog">
          <DialogHeader>
            <DialogTitle className="font-['Manrope'] text-xl flex items-center gap-2">
              <Shield className="w-5 h-5 text-[#34657f]" />
              Change roles for multiple users
            </DialogTitle>
            <DialogDescription>
              Paste email addresses (one per line, or separated by commas) and pick the role they should all get.
            </DialogDescription>
          </DialogHeader>

          {!roleResult ? (
            <div className="space-y-4">
              <div className="space-y-1.5">
                <Label htmlFor="role-emails">Email addresses</Label>
                <Textarea
                  id="role-emails"
                  rows={6}
                  value={roleEmails}
                  onChange={(e) => setRoleEmails(e.target.value)}
                  placeholder={"jane.doe@school.org\njohn.smith@school.org"}
                  data-testid="bulk-role-emails-input"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="role-target">New role</Label>
                <Select value={roleTarget} onValueChange={setRoleTarget}>
                  <SelectTrigger id="role-target" data-testid="bulk-role-target-select">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="instructor">Instructor</SelectItem>
                    <SelectItem value="skills_trainer">Skills Trainer</SelectItem>
                    <SelectItem value="team_admin">Program Administrator</SelectItem>
                    <SelectItem value="system_admin">System Admin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          ) : (
            <div className="space-y-3" data-testid="bulk-role-result">
              <div className="rounded-lg bg-green-50 border border-green-200 p-3 text-sm text-green-800">
                <strong>{roleResult.updated.length}</strong> user(s) updated to <strong>{getRoleBadge(roleTarget).label}</strong>.
              </div>
              {roleResult.updated.length > 0 && (
                <ul className="text-xs text-[#5C5C5C] space-y-0.5 max-h-32 overflow-auto">
                  {roleResult.updated.map((u, i) => (
                    <li key={i}>{u.name} ({u.email}) — {getRoleBadge(u.old_role).label} → {getRoleBadge(u.new_role).label}</li>
                  ))}
                </ul>
              )}
              {roleResult.not_found?.length > 0 && (
                <div className="rounded-lg bg-amber-50 border border-amber-200 p-3">
                  <p className="text-sm font-medium text-amber-800 mb-1">Not found:</p>
                  <ul className="text-xs text-amber-800 space-y-0.5 max-h-24 overflow-auto">
                    {roleResult.not_found.map((e, i) => <li key={i}>{e}</li>)}
                  </ul>
                </div>
              )}
              {roleResult.skipped_self?.length > 0 && (
                <p className="text-xs text-amber-700">Skipped your own account to keep you from locking yourself out.</p>
              )}
            </div>
          )}

          <DialogFooter>
            {!roleResult ? (
              <>
                <Button variant="outline" onClick={() => setRoleChangeOpen(false)} data-testid="bulk-role-cancel-btn">Cancel</Button>
                <Button className="bg-[#34657f] hover:bg-[#2a5060]" onClick={handleBulkRoleChange} disabled={roleLoading} data-testid="bulk-role-submit-btn">
                  {roleLoading ? 'Updating…' : 'Update roles'}
                </Button>
              </>
            ) : (
              <Button className="bg-[#34657f] hover:bg-[#2a5060]" onClick={() => setRoleChangeOpen(false)} data-testid="bulk-role-done-btn">Done</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default UsersPage;
